/**
 * Per-tab history for Nextcloud folder and file opens.
 * Back/Forward restores path, kind, and surface on the one Nextcloud tab.
 */

import { useEffect, useState } from 'react'
import { dispatchNextcloudOpen } from '@/components/integrations/nextcloud/nextcloud-browse-events'
import type { OpenTab } from '@/components/features/document-tabs'
import type { NavigationEntry } from '@/hooks/use-navigation-history'
import { navigationFieldsFromOpenTab, type IntegrationNavFields } from '@/lib/basecamp-integration-nav'

export const NEXTCLOUD_SHELL_HISTORY_EVENT = 'maater-nextcloud-shell-history'

type NextcloudHistoryFields = IntegrationNavFields & {
  nextcloudPath: string
  nextcloudKind: 'file' | 'folder'
  nextcloudSurface: 'browse' | 'document'
}

type NavRecorder = {
  getCurrentEntry: (slotId: string) => NavigationEntry | null
  navigate: (slotId: string, entry: IntegrationNavFields, caller?: string) => void
}

let shellCanBack = false
let shellCanForward = false
const shellListeners = new Set<() => void>()

export function setNextcloudShellNav(canBack: boolean, canForward: boolean) {
  if (shellCanBack === canBack && shellCanForward === canForward) return
  shellCanBack = canBack
  shellCanForward = canForward
  shellListeners.forEach((listener) => listener())
}

export function useNextcloudShellNav() {
  const [state, setState] = useState({ canBack: shellCanBack, canForward: shellCanForward })
  useEffect(() => {
    const listener = () => setState({ canBack: shellCanBack, canForward: shellCanForward })
    shellListeners.add(listener)
    listener()
    return () => {
      shellListeners.delete(listener)
    }
  }, [])
  return state
}

export function dispatchNextcloudShellHistory(direction: 'back' | 'forward') {
  window.dispatchEvent(new CustomEvent(NEXTCLOUD_SHELL_HISTORY_EVENT, { detail: direction }))
}

export function nextcloudFieldsFromTab(tab: OpenTab): NextcloudHistoryFields {
  const data = tab.integrationData
  const surface = data?.nextcloudSurface || 'browse'
  const organizationId = data?.organizationId ?? tab.workspaceId ?? undefined
  return {
    documentId: tab.id,
    viewMode: 'single',
    tabType: 'integration-nextcloud',
    title: tab.title,
    ...(organizationId ? { organizationId } : {}),
    nextcloudPath: data?.nextcloudPath || '/',
    nextcloudKind: data?.nextcloudKind || 'folder',
    nextcloudSurface: surface,
    ...(data?.nextcloudFileId ? { nextcloudFileId: data.nextcloudFileId } : {}),
    ...(data?.nextcloudPreviewPath ? { nextcloudPreviewPath: data.nextcloudPreviewPath } : {}),
  }
}

export function isNextcloudHistoryEntry(
  entry: NavigationEntry | null | undefined,
): entry is NavigationEntry & NextcloudHistoryFields {
  return Boolean(
    entry &&
      entry.tabType === 'integration-nextcloud' &&
      typeof entry.nextcloudPath === 'string',
  )
}

function sameNextcloudEntry(
  entry: NavigationEntry | null | undefined,
  fields: NextcloudHistoryFields,
): boolean {
  if (!isNextcloudHistoryEntry(entry)) return false
  return (
    entry.nextcloudPath === fields.nextcloudPath &&
    (entry.nextcloudKind || 'folder') === fields.nextcloudKind &&
    (entry.nextcloudSurface || 'browse') === fields.nextcloudSurface &&
    (entry.nextcloudPreviewPath || '') === (fields.nextcloudPreviewPath || '')
  )
}

export function recordNextcloudNavigation(
  navigation: NavRecorder,
  slotId: string,
  previous: OpenTab | null,
  next: OpenTab,
) {
  const nextFields = nextcloudFieldsFromTab(next)
  const tip = navigation.getCurrentEntry(slotId)
  if (sameNextcloudEntry(tip, nextFields)) return
  if (previous?.type === 'integration-nextcloud' && previous.id === next.id) {
    const currentFields = nextcloudFieldsFromTab(previous)
    if (!sameNextcloudEntry(tip, currentFields)) {
      navigation.navigate(slotId, currentFields, 'nextcloud-seed-current')
    }
  } else if (previous && previous.id !== next.id) {
    if (!tip || tip.documentId !== previous.id) {
      navigation.navigate(slotId, navigationFieldsFromOpenTab(previous), 'nextcloud-seed-previous')
    }
  }
  navigation.navigate(slotId, nextFields, 'nextcloud-open')
}

export function applyNextcloudHistoryEntry(args: {
  openTabs: OpenTab[]
  activeTabId: string | null
  activeSlotId: string
  entry: NavigationEntry
}): { tabs: OpenTab[]; nextActiveTabId: string } | null {
  const { openTabs, activeTabId, activeSlotId, entry } = args
  if (!isNextcloudHistoryEntry(entry)) return null
  const existing =
    openTabs.find((tab) => tab.type === 'integration-nextcloud' && tab.tabSlotId === activeSlotId) ??
    openTabs.find((tab) => tab.type === 'integration-nextcloud') ??
    openTabs.find((tab) => tab.id === activeTabId)
  if (!existing) return null
  const title =
    entry.title ||
    entry.nextcloudPath.split('/').filter(Boolean).pop() ||
    'Nextcloud'
  const tabs = openTabs.map((tab) =>
    tab.id === existing.id
      ? {
          ...tab,
          id: 'integration-nextcloud',
          title,
          type: 'integration-nextcloud' as const,
          isDirty: false,
          tabSlotId: activeSlotId,
          workspaceId: entry.organizationId ?? tab.workspaceId,
          integrationData: {
            integrationId: 'nextcloud',
            ...(entry.organizationId ? { organizationId: entry.organizationId } : {}),
            nextcloudPath: entry.nextcloudPath,
            nextcloudKind: entry.nextcloudKind || 'folder',
            nextcloudSurface: entry.nextcloudSurface || 'browse',
            ...(entry.nextcloudFileId ? { nextcloudFileId: entry.nextcloudFileId } : {}),
            ...(entry.nextcloudPreviewPath ? { nextcloudPreviewPath: entry.nextcloudPreviewPath } : {}),
          },
        }
      : tab,
  )
  return { tabs, nextActiveTabId: 'integration-nextcloud' }
}

/** Tell the sidebar which row matches a restored history step. */
export function announceNextcloudHistorySelection(entry: NavigationEntry) {
  if (!isNextcloudHistoryEntry(entry) || !entry.organizationId) return
  dispatchNextcloudOpen({
    organizationId: entry.organizationId,
    path: entry.nextcloudPath,
    kind: entry.nextcloudKind || 'folder',
    name: entry.title || 'Nextcloud',
    ...(entry.nextcloudFileId ? { fileId: entry.nextcloudFileId } : {}),
    ...(entry.nextcloudPreviewPath ? { previewPath: entry.nextcloudPreviewPath } : {}),
    surface: entry.nextcloudSurface || 'browse',
    selectOnly: true,
  })
}
